import Loading from '@components/shared/Loading';
import { SettingsContext } from '@contexts/SettingsContext';
import { Alert } from '@mui/material';
import { StyledEngineProvider } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import classNames from 'classnames';
import { SnackbarProvider } from 'notistack';
import { StrictMode, useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import 'regenerator-runtime/runtime';
import ErrorBoundary from '../components/ErrorBoundary';
import '../configurations/highcharts';
import store from '../configurations/store';
import '../styles/application.scss';
import '../styles/application_standalone.scss';
import '../styles/legacy.scss';
import { useGetInitialSetupQuery } from '@components/rootApi';
import { initializeSentry } from '@configurations/sentry';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { apiConfig } from '@helpers/pathHelper';
import type {} from '@mui/lab/themeAugmentation';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { ThemeProvider } from './ThemeProvider';
import { setImpersonationAccessToken } from '@features/auth/impersonation';

// Hack to ensure settings object exists
window.settings = {} as unknown as typeof window.settings;

if (typeof window !== 'undefined') {
  window.lciDashboardVersion = process.env.APP_VERSION ?? 'unknown';
}

type SettingsWrapperProps = {
  apiHost?: string;
  hubMode?: boolean;
  children: React.ReactNode;
  impersonationAccessToken?: string;
};

const SettingsWrapper = (props: SettingsWrapperProps) => {
  useEffect(() => {
    apiConfig.host = props.apiHost ?? process.env.API_HOST ?? '';
  }, [props.apiHost]);
  const { data: settings, isFetching } = useGetInitialSetupQuery();
  const [isSettingsLoaded, setIsSettingsLoaded] = useState(false);

  setImpersonationAccessToken(props.impersonationAccessToken);

  useEffect(() => {
    if (settings) {
      window.settings = { ...window.settings, ...settings };
      initializeSentry(settings.release, settings.deployment_env);

      setIsSettingsLoaded(true);
    }
  }, [settings]);

  if (isFetching || !isSettingsLoaded) return <Loading />;

  if (!settings) return <Alert severity="error">Error loading settings</Alert>;

  if (settings.user) {
    Sentry.setUser({ id: settings.user.id.toString() });
  }

  return (
    <SettingsContext.Provider
      value={{
        ...window.settings,
        hubMode: !!props.hubMode,
      }}
    >
      <ErrorBoundary>
        <div
          id="lci-dashboard-root"
          className={classNames('nd-container', {
            admin: window.settings.admin,
          })}
        >
          {props.children}
        </div>
      </ErrorBoundary>
    </SettingsContext.Provider>
  );
};

export type ApplicationProviderProps = SettingsWrapperProps;

export const ApplicationProvider = (props: ApplicationProviderProps) => (
  <StrictMode>
    <Provider store={store}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <SnackbarProvider
              anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
            >
              <SettingsWrapper {...props} />
            </SnackbarProvider>
          </LocalizationProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </Provider>
  </StrictMode>
);
